import { ComponentProps, useEffect, useState } from 'react';
import { TextField } from '@mui/material';

interface NumericInputProps extends Omit<ComponentProps<typeof TextField>, 'value' | 'onChange' | 'onBlur' | 'onKeyDown' | 'min' | 'max' | 'step' > {
    value: number | undefined;
    onChange?: (value: number | undefined) => void;
    allowDecimals?: boolean;
    min?: number;
    max?: number;
    step?: number;
    onBlur?: (value: number | undefined, event: React.FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>, value: number | undefined) => void;
}

export const NumericInput: React.FC<NumericInputProps> = ({
    label,
    value,
    onChange,
    allowDecimals = false,
    min,
    max,
    placeholder,
    step,
    required = false,
    onBlur,
    onKeyDown,
}) => {
    const [inputValue, setInputValue] = useState<string>(value?.toString() || '');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setInputValue(value === undefined || Number.isNaN(value) ? '' : String(value));
    }, [value]);

    const isStepValid = (num: number, stepValue: number): boolean => {
        const tolerance = 1e-10;

        return Math.abs(num % stepValue) < tolerance || Math.abs(num % stepValue - stepValue) < tolerance;
    };

    const parseCurrent = (raw: string): { parsed: number | undefined; error: string | null } => {
        if (raw === '') {
            return required ? { parsed: undefined, error: 'This field is required' } : { parsed: undefined, error: null };
        }
        if (allowDecimals && raw === '.') {
            return { parsed: undefined, error: null };
        }
        const parsedValue = allowDecimals ? parseFloat(raw) : parseInt(raw, 10);

        if (Number.isNaN(parsedValue)) {
            return { parsed: undefined, error: 'Invalid number' };
        }
        if (min !== undefined && parsedValue < min) {
            return { parsed: undefined, error: `Value must be at least ${min}` };
        }
        if (max !== undefined && parsedValue > max) {
            return { parsed: undefined, error: `Value must be at most ${max}` };
        }
        if (step !== undefined && !isStepValid(parsedValue, step)) {
            return { parsed: undefined, error: `Value must be a multiple of ${step}` };
        }
        return { parsed: parsedValue, error: null };
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const input = e.target.value;

        setInputValue(input);

        const { parsed, error: nextError } = parseCurrent(input);

        setError(nextError);
        if (onChange) {
            onChange(parsed);
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
        const { parsed, error: nextError } = parseCurrent(inputValue);

        setError(nextError);
        if (onBlur) {
            onBlur(parsed, e);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        const { parsed } = parseCurrent(inputValue);

        if (onKeyDown) {
            onKeyDown(e, parsed);
        }
    };

    return (
        <TextField
            error={!!error}
            helperText={error}
            label={label}
            placeholder={placeholder}
            required={required}
            size="small"
            value={inputValue}
            onBlur={handleBlur}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
        />
    );
};
